import React from 'react'
import { Link } from 'gatsby'
import {
  Header,
  Layout,
  Intro,
  SectionTitle,
  Grid,
  GridItem,
} from '../../../../components'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Feedback and error messages will interrupt a user's task and draw
            their attention to an important contextual or actionable message.
            First, decide what type of feedback you are trying to communicate,
            then decide which component or pattern to use to communicate it.
          </p>
        </Intro>

        <SectionTitle>Information Feedback</SectionTitle>
        <p>
          This type of feedback provides helpful information that the user does
          not need to act on. Information feedback functions similarly to words
          in parentheses. The experience should feel complete without this
          information, but it should still enrich the user's understanding in
          some way.
        </p>
        <h3>Visual Treatments for Information Feedback</h3>
        <p>
          If it is system-level information that does not directly impact the
          user's current action on the page:
        </p>
        <ul class="c-unordered-list">
          <li class="c-unordered-list__item">
            use the <strong>Information - Page Level</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component
          </li>
          <li class="c-unordered-list__item">
            allow the notification to be dismissed if necessary
          </li>
        </ul>
        <p>
          If it is information that directly impacts the user's current action
          on the page:
        </p>
        <ul class="c-unordered-list">
          <li class="c-unordered-list__item">
            use the <strong>Information - Page Level</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component for information that clarifies two or more parts of the
            page
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">
                allow the notification to be dismissed if necessary
              </li>
            </ul>
          </li>
          <li class="c-unordered-list__item">
            use the <strong>Information - Inline</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component for information that clarifies a single part of the page
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">
                place the inline notification close to the part of the page that
                it clarifies
              </li>
            </ul>
          </li>
        </ul>
        <p>
          If it is information related to a page-level action the user just
          completed, consider using the{' '}
          <Link class="link" to={'/' + data.site + '/patterns/modal/design'}>
            Modal
          </Link>{' '}
          pattern.
        </p>
        <h3>Writing Information Feedback</h3>
        <p>
          Information feedback is descriptive by definition and will usually not
          include an action.
        </p>
        <Grid>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i>Do lead with a description of
                the potential future problem.
              </div>
              <div class="c-notification--inline-info">
                <div class="c-notification__icon">
                  <i class="icon icon--info u-color--graphic-blue-base"></i>
                </div>
                <div class="c-notification__content">
                  You must have enough money in your available balance to cover
                  this transfer when we attempt it on Thu Apr 23 2020, or the
                  money will not be moved from the account.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i>Don't lead with contextual
                information
              </div>
              <div class="c-notification--inline-info">
                <div class="c-notification__icon">
                  <i class="icon icon--info u-color--graphic-blue-base"></i>
                </div>
                <div class="c-notification__content">
                  We will attempt this transfer on Thu Apr 23 2020. The money
                  will not be moved from your account if you do not have enough
                  funds in your available balance.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do use active voice and words
                like "you" and "your" to direct the user to any actions.
              </div>
              <div class="c-notification--inline-info">
                <div class="c-notification__icon">
                  <i class="icon icon--info u-colo--blue-base"></i>
                </div>
                <div class="c-notification__content">
                  We aren't able to confirm this mailing address in our system.
                  You can try again later or continue to address this without
                  confirming it.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use passive voice or
                ambiguous language
              </div>
              <div class="c-notification--inline-info">
                <div class="c-notification__icon">
                  <i class="icon icon--info u-color--graphic-blue-base"></i>
                </div>
                <div class="c-notification__content">
                  Unable to confirm address.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do repeat nouns and verbs
                from the main interface so the user can clearly relate the
                warning to the action on the page.
              </div>
              <div class="c-notification--inline-info">
                <div class="c-notification__icon">
                  <i class="icon icon--info u-color--graphic-blue-base"></i>
                </div>
                <div class="c-notification__content">
                  Any changes you make to this payee will affect all pending,
                  scheduled and future payments.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use generic of unspecific
                terms.
              </div>
              <div class="c-notification--inline-info">
                <div class="c-notification__icon">
                  <i class="icon icon--info u-color--graphic-blue-base"></i>
                </div>
                <div class="c-notification__content">
                  Updating this record may disrupt your transactions.
                </div>
              </div>
            </div>
          </GridItem>
        </Grid>
        <hr />
        <SectionTitle>Warning Feedback</SectionTitle>
        <p>
          This type of feedback provides information that the user does not need
          to act on immediately, but may need to consider to avoid a problem at
          some point in the future. Unlike error messages, warning feedback does
          not block the user from completing their current action.
        </p>
        <h3>Visual Treatement for Warning Feedback</h3>
        <p>
          If it is a system-level warning that does not directly impact the
          user's current action on the page:
        </p>
        <ul class="c-unordered-list">
          <li class="c-unordered-list__item">
            use the <strong>Warning - Page Level</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component
          </li>
          <li class="c-unordered-list__item">
            allow the notification to be dismissed if necessary
          </li>
        </ul>
        <p>
          If it is a warning that directly impacts the user's current action on
          the page:
        </p>
        <ul class="c-unordered-list">
          <li class="c-unordered-list__item">
            use the <strong>Warning - Page Level</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            for a warning about two or more parts of the page
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">
                allow the notification to be dismissed if necessary
              </li>
            </ul>
          </li>
          <li class="c-unordered-list__item">
            use the <strong>Warning - Inline</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component for a warning about a single part of the page
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">
                place the inline notification close to the part of the page that
                needs a warning
              </li>
            </ul>
          </li>
        </ul>
        <p>
          If it is a warning related to a page-level action the user just
          completed, consider using the{' '}
          <Link class="link" to={'/' + data.site + '/patterns/modal/design'}>
            Modal
          </Link>{' '}
          pattern.
        </p>
        <h3>Writing Warning Feedback</h3>
        <p>
          Warning feedback should focus on helping the user understand the
          future problem and how to avoid it.
        </p>
        <Grid>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i>Do lead with a description of
                the potential future problem.
              </div>
              <div class="c-notification--inline-warning">
                <div class="c-notification__icon">
                  <i class="icon icon--exclamation u-color--semantic-orange-base"></i>
                </div>
                <div class="c-notification__content">
                  You must have enough money in your available balance to cover
                  this transfer when we attempt it on Thu Apr 23 2020, or the
                  money will not be moved from the account.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i>Don't lead with contextual
                information
              </div>
              <div class="c-notification--inline-warning">
                <div class="c-notification__icon">
                  <i class="icon icon--exclamation u-color--semantic-orange-base"></i>
                </div>
                <div class="c-notification__content">
                  We will attempt this transfer on Thu Apr 23 2020. The money
                  will not be moved from your account if you do not have enough
                  funds in your available balance.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do use active voice and words
                like "you" and "your" to direct the user to any actions.
              </div>
              <div class="c-notification--inline-warning">
                <div class="c-notification__icon">
                  <i class="icon icon--exclamation u-color--semantic-orange-base"></i>
                </div>
                <div class="c-notification__content">
                  We aren't able to confirm this mailing address in our system.
                  You can try again later or continue to address this without
                  confirming it.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use passive voice or
                ambiguous language
              </div>
              <div class="c-notification--inline-warning">
                <div class="c-notification__icon">
                  <i class="icon icon--exclamation u-color--semantic-orange-base"></i>
                </div>
                <div class="c-notification__content">
                  Unable to confirm address.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do repeat nouns and verbs
                from the main interface so the user can clearly relate the
                warning to the action on the page.
              </div>
              <div class="c-notification--inline-warning">
                <div class="c-notification__icon">
                  <i class="icon icon--exclamation u-color--semantic-orange-base"></i>
                </div>
                <div class="c-notification__content">
                  Any changes you make to this payee will affect all pending,
                  scheduled and future payments.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use generic of unspecific
                terms.
              </div>
              <div class="c-notification--inline-warning">
                <div class="c-notification__icon">
                  <i class="icon icon--exclamation u-color--semantic-orange-base"></i>
                </div>
                <div class="c-notification__content">
                  Updating this record may disrupt your transactions.
                </div>
              </div>
            </div>
          </GridItem>
        </Grid>

        <hr />
        <SectionTitle>Error Messages</SectionTitle>
        <p>
          Error messages tell the user when they are unable to complete an
          action, and points them to the solution. The user will be blocked from
          completing their current action until the error is resolved.
        </p>
        <h3>Visual Treatments for Error Messages</h3>
        <p>
          If it is a system-level error unrelated to the user's current action
          on the page:
        </p>
        <ul class="c-unordered-list">
          <li class="c-unordered-list__item">
            use the <strong>Error - Page Level</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component
          </li>
          <li class="c-unordered-list__item">
            the notification cannot be dismissed
          </li>
        </ul>
        <p>
          If it is an error preventing an action the user has just attempted to
          take or is about to take on the page:
        </p>
        <ul class="c-unordered-list">
          <li class="c-unordered-list__item">
            use the <strong>Error - Page Level</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            for an error that the user can resolve from two or more parts of the
            page
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">
                the notification cannot be dismissed
              </li>
            </ul>
          </li>
          <li class="c-unordered-list__item">
            use the <strong>Error - Inline</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component for an error that the user can resolve from a single part
            of the page
            <ul class="c-unordered-list">
              <li class="c-unordered-list__item">
                place the inline notification close to the part where the user
                can resolve the error
              </li>
            </ul>
          </li>
          <li class="c-unordered-list__item">
            use the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/inline-error/design'}
            >
              Input Validation
            </Link>{' '}
            component for an error that the user can resolve from a single Input
            Field
          </li>
        </ul>
        <p>
          If the user needs more robust information about the error, or a more
          forceful interruption of the page-level action they are trying to
          complete, consider designing a full error page.
        </p>
        <h3>Writing Error Messages</h3>
        <p>
          Error messages need to explain why a user can't complete an action and
          provide a solution whouth making them feel like they've done something
          wrong. Sometimes the only solution is to try again later or to call
          our customer service number, but we should try to be more specific
          when we can.
        </p>
        <Grid>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i>Do lead with the action to be
                taken if there is one. Otherwise, lead with a description of the
                error.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  Enter an amount between $0.01 and $999,999.99 using only
                  numbers and a decimal point.
                </div>
              </div>
              <div class="c-notification--inline-error l-margin-top--large">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  The account you selected doesn't have enough funds to make
                  this transfer.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i>Don't lead with the consequence.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  You won't be able to make this transfer until you have enough
                  funds in your account.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do use commands and, where
                possible, link to any actions the user needs to take.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  You already have an identical payment scheduled for this date,
                  amount and payee. Edit or cancel a scheduled payment in your{' '}
                  <Link class="link">bill pay activity</Link>.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use passive voice or
                ambiguous language.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  Duplicate payment could not be added.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do use "sorry" and "please"
                when an error is our fault or when the solution will take extra
                effort from the user.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  Sorry, we're not able to schedule this payment right now.
                  Please try again a little later.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use "sorry" or "please"
                when an error is simple to resolve.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  Sorry, this entry is invalid. Please enter a zip code that's
                  five or nine digits.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do repeat nouns and verbs
                from the main interface so the user can clearly relate the error
                to the action on the page.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  You have already added this payee using the same account
                  number.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use generic or unspecific
                terms.
              </div>
              <div class="c-notification--inline-error">
                <div class="c-notification__icon">
                  <i class="icon icon--error u-color--semantic-red-base"></i>
                </div>
                <div class="c-notification__content">
                  This record has a repeat name and number.
                </div>
              </div>
            </div>
          </GridItem>
        </Grid>

        <hr />
        <SectionTitle>Success Feedback</SectionTitle>
        <p>
          This type of feedback provides confirmation that the user has
          successfully completed their previous action. It can also be used to
          offer follow-up actions to the user.
        </p>
        <h3>Visual Treatments for Success Feedback</h3>
        <p>
          If the user just completed a page-level action, or a major action
          within the page:
        </p>
        <ul class="c-unordered-list">
          <li class="c-unordered-list__item">
            use the <strong>Success - Page Level</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component
          </li>
          <li class="c-unordered-list__item">
            the notification is always dismissible
          </li>
        </ul>
        <p>If the user just completed a minor action within the page:</p>
        <ul class="c-unordered-list">
          <li>
            use the <strong>Success - Inline</strong> variation of the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/notifications/design'}
            >
              Notification
            </Link>{' '}
            component, and place it near where the user completed the action
          </li>
        </ul>
        <p>
          If the user needs more robust information about the page-level action
          they just completed, consider designing a full confirmation page.
        </p>
        <h3>Writing Success Feedback</h3>
        <Grid>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i>Do state the completed action
                simply.
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  You have scheduled a transfer for Thu Apr 23 2020.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i>Don't use the word
                "successfully". It's redundant.
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  You have successfully scheduled a transfer for Thu Apr 23
                  2020.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Link directly to details of
                the successfull action and any follow-up actions, or present
                them elsewhere on the page.
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  You have scheduled a transfer series starting Thu Apr 23 2020.
                  View the details on your{' '}
                  <Link class="link">transfer activity page</Link>.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't try to pack too much into
                the success message.
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  You have scheduled a transfer for Thu Apr 23 2020. It will
                  happen from My Choice Checking (...7890) to Relationship
                  Savings (...1234) in the amount of $500. This transfer will
                  recur weekly until Fri Apr 23 2021. You can choose to edit
                  these details at any time before Wed Apr 22 2020 by finding
                  this transaction in your transfer activity.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do use active voice and
                address the user directly with words like "you" and "your".
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  You have canceled your payment to Bob's Garage scheduled for
                  Thu Apr 23 2020.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use passive voice or
                ambiguous language.
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  This payment to Bob's Garage on Thu Apr 23 2020 will not be
                  processed.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="do">
              <div class="do__title">
                <i class="icon icon--success"></i> Do repeat nouns and verbs
                from the main interface so the user can clearly relate the error
                to the action on the page.
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  You have added Jimmy the Plumber for paper check payment.
                </div>
              </div>
            </div>
          </GridItem>
          <GridItem columns="6">
            <div class="dont">
              <div class="dont__title">
                <i class="icon icon--error"></i> Don't use generic or unspecific
                terms.
              </div>
              <div class="c-notification--inline-success">
                <div class="c-notification__icon">
                  <i class="icon icon--success u-color--semantic-green-base"></i>
                </div>
                <div class="c-notification__content">
                  The new payee will be paid by check.
                </div>
              </div>
            </div>
          </GridItem>
        </Grid>
      </Layout>
    </>
  )
}
